import deliveryStatusType, { DeliveryStatus } from 'constants/deliveryStatus';
import { useEffect, useRef } from 'react';
import { useWtrDispatch, useWtrSelector } from 'redux/hooks';
import { getDeliveryStatusByOrderId } from 'redux/modules/delivery-tracking/selectors';
import { isEligibleForDeliveryTracking } from 'redux/modules/order/selectors/is-eligible-for-delivery-tracking';
import { getDeliveryStatus } from 'redux/modules/orders/actions/get-delivery-status';
import { getSlotByOrderId, getStatus } from 'redux/modules/orders/selectors/get-order';
import { isSignedOnAsCustomer } from 'redux/modules/sessions/selectors';
import { log } from 'utils/error-logging';

function isOrderFinished(deliveryTrackingStatus: DeliveryStatus | undefined): boolean {
  return (
    deliveryTrackingStatus === deliveryStatusType.DELIVERED ||
    deliveryTrackingStatus === deliveryStatusType.CANCELLED ||
    deliveryTrackingStatus === deliveryStatusType.MISSED
  );
}

function clearCurrentInterval(ref: React.MutableRefObject<NodeJS.Timeout | null>) {
  if (ref.current) {
    clearInterval(ref.current);
    ref.current = null;
  }
}

const DeliveryTrackingPollIntervalMs = 300000; // 5 minutes;

function useDeliveryTrackingPoll(orderId: string) {
  const dispatch = useWtrDispatch();
  const status = useWtrSelector((state: WtrState) => getStatus(state, orderId));
  const signedOnAsCustomer = useWtrSelector(isSignedOnAsCustomer);
  const deliveryStatus = useWtrSelector((state: WtrState) =>
    getDeliveryStatusByOrderId(state, orderId),
  );
  const orderSlot = useWtrSelector((state: WtrState) => getSlotByOrderId(state, orderId));
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (
      !signedOnAsCustomer &&
      isEligibleForDeliveryTracking(status, orderSlot) &&
      !isOrderFinished(deliveryStatus) &&
      !intervalRef.current
    ) {
      intervalRef.current = setInterval(() => {
        dispatch(getDeliveryStatus(orderId))
          .then(result => {
            if (isOrderFinished(result?.status)) {
              clearCurrentInterval(intervalRef);
            }
          })
          .catch(err => {
            log(err, { logger: 'orders/delivery-tracking-update', section: 'orders' });
            return null;
          });
      }, DeliveryTrackingPollIntervalMs);
    }

    return () => {
      clearCurrentInterval(intervalRef);
    };
  }, [dispatch, orderId, orderSlot, status, deliveryStatus, signedOnAsCustomer]);
}

export default useDeliveryTrackingPoll;
